import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import {
  ASSETS,
  PHONE_TEL,
  PHONE_DISPLAY,
  WHATSAPP_URL,
} from "../data/site";

const NAV = [
  { label: "Serviços", id: "servicos" },
  { label: "Regiões", id: "regioes" },
  { label: "Sobre", id: "sobre" },
  { label: "Avaliações", id: "avaliacoes" },
  { label: "Contato", id: "contato" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-10 h-[72px] md:h-[84px]">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 absolute left-1/2 -translate-x-1/2 lg:static lg:left-auto lg:translate-x-0"
        >
          <img
            src={ASSETS.logo}
            alt="FS Guinchos"
            className="h-12 md:h-14 w-auto drop-shadow-[0_0_10px_rgba(230,0,0,0.35)]"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className="text-sm font-medium text-neutral-300 hover:text-white uppercase tracking-wide transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#e60000] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            data-testid="header-call-btn"
            href={PHONE_TEL}
            className="hidden sm:flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors"
          >
            <Phone size={16} />
            <span className="hidden md:inline">{PHONE_DISPLAY}</span>
          </a>
          <a
            data-testid="header-whatsapp-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-black px-4 md:px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors wa-glow"
          >
            <MessageCircle size={16} />
            <span className="hidden md:inline">WhatsApp</span>
            <span className="md:hidden">Chamar</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white ml-1"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#050505] border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => scrollTo(n.id)}
                  className="text-left py-3 text-lg font-medium text-neutral-200 border-b border-white/5 uppercase tracking-wide"
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
